import {
  useGetMunicipalityQuery,
  useGetStockholmConsumerProvidersQuery,
  type GetStockholmConsumerProvidersQuery,
} from "@appcaire/graphql";
import { STOCKHOLM_SLUG } from "./site";

export type StockholmProvider =
  GetStockholmConsumerProvidersQuery["providersForMunicipality"][number];

export function districtSlug(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase("sv-SE")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " och ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function uniqueProviders(
  providers: readonly StockholmProvider[],
): StockholmProvider[] {
  const byId = new Map<string, StockholmProvider>();
  for (const provider of providers) {
    if (!byId.has(provider.id)) byId.set(provider.id, provider);
  }
  return [...byId.values()].sort((left, right) =>
    left.name.localeCompare(right.name, "sv"),
  );
}

export function providerDistricts(provider: StockholmProvider): string[] {
  return Array.from(
    new Set(
      (provider.presence.geographicAreas ?? [])
        .map((area) => area.trim())
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right, "sv"));
}

export function collectDistricts(
  providers: readonly StockholmProvider[],
): Array<{ name: string; slug: string; count: number }> {
  const counts = new Map<string, { name: string; count: number }>();
  for (const provider of uniqueProviders(providers)) {
    for (const name of providerDistricts(provider)) {
      const slug = districtSlug(name);
      if (!slug) continue;
      const current = counts.get(slug);
      counts.set(slug, { name, count: (current?.count ?? 0) + 1 });
    }
  }
  return [...counts.entries()]
    .map(([slug, value]) => ({ slug, ...value }))
    .sort((left, right) => left.name.localeCompare(right.name, "sv"));
}

function latestPresenceDate(
  providers: readonly StockholmProvider[],
  select: (provider: StockholmProvider) => string | null | undefined,
): string | null {
  const dates = providers
    .map(select)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value))
    .filter((value) => !Number.isNaN(value.getTime()))
    .sort((left, right) => right.getTime() - left.getTime());
  return dates[0]?.toISOString() ?? null;
}

/** Latest change date reported by Stockholm's source, never the crawl time. */
export function latestSourceDate(
  providers: readonly StockholmProvider[],
): string | null {
  return latestPresenceDate(
    providers,
    (provider) => provider.presence.sourceUpdatedAt,
  );
}

/** Latest time Caire fetched a Stockholm source row. */
export function latestCrawlDate(
  providers: readonly StockholmProvider[],
): string | null {
  return latestPresenceDate(
    providers,
    (provider) => provider.presence.lastSyncedAt,
  );
}

export function formatSourceDate(value: string | null): string {
  if (!value) return "uppdateringsdatum saknas";
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(value));
}

export function useStockholmProviders() {
  // The stats resolver returns the complete municipality result when paging
  // variables are omitted. Keeping this as one Apollo query makes the full
  // result available to renderToStringWithData as well as browser hydration;
  // client-only fetchMore effects would leave raw SSR HTML truncated.
  const result = useGetStockholmConsumerProvidersQuery();
  const providers = uniqueProviders(
    result.data?.providersForMunicipality ?? [],
  );
  return {
    ...result,
    providers,
    districts: collectDistricts(providers),
    sourceDate: latestSourceDate(providers),
    crawlDate: latestCrawlDate(providers),
  };
}

export function useStockholmMunicipality() {
  return useGetMunicipalityQuery({
    variables: { slug: STOCKHOLM_SLUG },
  });
}
